/**
 * Connector Schema Definitions
 *
 * Spec-first schema definitions for channel/provider/connection management.
 * These schemas drive oRPC type generation and validation.
 */

import { z } from "zod";
import {
  UuidField,
  ParticipantIdField,
  TimestampField,
  ErrorResponse,
} from "./common";

// Core Channel entity (communication medium)
const Channel = z.object({
  id: UuidField,
  organizationId: UuidField,
  name: z.string().min(1).max(200),
  type: z.string().min(1).max(100), // sms, email, whatsapp, discord, slack, etc.
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

// Core Provider entity (service implementation)
const Provider = z.object({
  id: UuidField,
  organizationId: UuidField,
  name: z.string().min(1).max(200),
  type: z.string().min(1).max(100), // twilio, sendgrid, api, local, sdk, etc.
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

// Core Connection entity (channel + provider pairing)
const Connection = z.object({
  id: UuidField,
  workspaceId: UuidField,
  channelId: UuidField,
  providerId: UuidField,
  name: z.string().min(1).max(200),
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

// Event schemas for Channel
const ChannelCreateEvent = z.object({
  action: z.literal("created"),
  channel: Channel,
  timestamp: TimestampField,
});

const ChannelUpdateEvent = z.object({
  action: z.literal("updated"),
  channel: Channel,
  timestamp: TimestampField,
});

const ChannelDeleteEvent = z.object({
  action: z.literal("deleted"),
  channelId: UuidField,
  organizationId: UuidField,
  timestamp: TimestampField,
});

// Event schemas for Provider
const ProviderCreateEvent = z.object({
  action: z.literal("created"),
  provider: Provider,
  timestamp: TimestampField,
});

const ProviderUpdateEvent = z.object({
  action: z.literal("updated"),
  provider: Provider,
  timestamp: TimestampField,
});

const ProviderDeleteEvent = z.object({
  action: z.literal("deleted"),
  providerId: UuidField,
  organizationId: UuidField,
  timestamp: TimestampField,
});

// Event schemas for Connection
const ConnectionCreateEvent = z.object({
  action: z.literal("created"),
  connection: Connection,
  timestamp: TimestampField,
});

const ConnectionUpdateEvent = z.object({
  action: z.literal("updated"),
  connection: Connection,
  timestamp: TimestampField,
});

const ConnectionDeleteEvent = z.object({
  action: z.literal("deleted"),
  connectionId: UuidField,
  workspaceId: UuidField,
  channelId: UuidField,
  providerId: UuidField,
  timestamp: TimestampField,
});

// Invitation Status
const InvitationStatus = z.enum([
  "pending",
  "consumed",
  "expired",
  "revoked",
  "invalid",
]);

// Core Invitation entity (time-limited invite code for external threads)
const Invitation = z.object({
  id: UuidField,
  code: z.string().min(1).max(50),
  channelId: UuidField,
  /** The workspace the invitation belongs to (R78 Q69a): its members alone see and use it. */
  workspaceId: UuidField,
  conversationId: UuidField,
  fromParticipantId: ParticipantIdField,
  toExternalThreadId: z.string().min(1).max(500).optional(),
  status: InvitationStatus,
  expiresAt: TimestampField,
  createdByParticipantId: ParticipantIdField,
  consumedAt: TimestampField.optional(),
  consumedByExternalParticipantId: z.string().min(1).max(500).optional(),
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

// Core ThreadMapping entity (external thread ID to internal thread ID mapping)
const ThreadMapping = z.object({
  id: UuidField,
  externalThreadId: z.string().min(1).max(500),
  internalThreadId: UuidField,
  channelId: UuidField,
  /** The workspace the mapped thread belongs to (R78 Q69a). */
  workspaceId: UuidField,
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

// Event schemas for Invitation
const InvitationCreateEvent = z.object({
  action: z.literal("created"),
  invitation: Invitation,
  timestamp: TimestampField,
});

const InvitationConsumedEvent = z.object({
  action: z.literal("consumed"),
  invitation: Invitation,
  mapping: ThreadMapping,
  externalThreadId: z.string().min(1).max(500),
  externalParticipantId: z.string().min(1).max(500),
  internalThreadId: UuidField,
  conversationId: UuidField,
  channelId: UuidField,
  timestamp: TimestampField,
});

const InvitationRevokedEvent = z.object({
  action: z.literal("revoked"),
  invitationId: UuidField,
  timestamp: TimestampField,
});

const InvitationInvalidatedEvent = z.object({
  action: z.literal("invalidated"),
  invitationIds: z.array(UuidField),
  reason: z.string(),
  timestamp: TimestampField,
});

// Event schemas for ThreadMapping
const ThreadMappingCreatedEvent = z.object({
  action: z.literal("created"),
  mapping: ThreadMapping,
  timestamp: TimestampField,
});

const ThreadMappingUpdatedEvent = z.object({
  action: z.literal("updated"),
  oldMapping: ThreadMapping,
  newMapping: ThreadMapping,
  timestamp: TimestampField,
});

const ThreadMappingRemovedEvent = z.object({
  action: z.literal("removed"),
  externalThreadId: z.string().min(1).max(500),
  channelId: UuidField,
  timestamp: TimestampField,
});

// Export all schemas
export const schemas = {
  Channel,
  Provider,
  Connection,
  ChannelCreateEvent,
  ChannelUpdateEvent,
  ChannelDeleteEvent,
  ProviderCreateEvent,
  ProviderUpdateEvent,
  ProviderDeleteEvent,
  ConnectionCreateEvent,
  ConnectionUpdateEvent,
  ConnectionDeleteEvent,
  InvitationStatus,
  Invitation,
  ThreadMapping,
  InvitationCreateEvent,
  InvitationConsumedEvent,
  InvitationRevokedEvent,
  InvitationInvalidatedEvent,
  ThreadMappingCreatedEvent,
  ThreadMappingUpdatedEvent,
  ThreadMappingRemovedEvent,
  ErrorResponse,
};

// oRPC Type Inference Support
export type SchemaTypes = {
  [K in keyof typeof schemas]: z.infer<(typeof schemas)[K]>;
};

export type { SchemaTypes as ConnectorSchemaTypes };
export { schemas as connectorSchemas };
